import { useEffect, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { businessApi, bookingApi } from '../services/api';
import { Business, Booking } from '../types';
import axios from 'axios';

interface DashboardStats {
  reservasHoy: number;
  reservasMes: number;
  ingresosMes: number;
  clientesActivos: number;
  ocupacion: number;
  currency: string;
  reservasPorDia: { fecha: string; total: number }[];
  serviciosTop: { nombre: string; reservas: number; precio: number }[];
  reservasPorEstado: { estado: string; total: number; color: string }[];
  ingresosPorSemana: { semana: string; ingresos: number }[];
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: '€', USD: '$', GBP: '£', MAD: 'DH',
};

const STATUS_LABELS: Record<string, string> = {
  CONFIRMED: 'Confirmadas', COMPLETED: 'Completadas',
  PENDING: 'Pendientes', CANCELLED: 'Canceladas', NO_SHOW: 'No show',
};

function KpiCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color: string }) {
  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <p className="text-sm font-medium text-gray-500 mb-1">{label}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

export function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentBookings, setRecentBookings] = useState<Booking[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, businessesRes, bookingsRes] = await Promise.all([
        axios.get('/api/stats', { headers: { Authorization: `Bearer ${token}` } }),
        businessApi.getMyBusinesses(),
        bookingApi.getMyBookings({ limit: 8 }),
      ]);
      setStats(statsRes.data.data);
      setBusinesses(businessesRes.data.data.businesses || []);
      setRecentBookings(bookingsRes.data.data.bookings || []);
    } catch (e) {
      setError('Error cargando el dashboard');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  const sym = CURRENCY_SYMBOLS[stats?.currency || 'EUR'] || '€';

  return (
    <div className="space-y-6 p-1">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 text-sm mt-1">Resumen de tu negocio este mes</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Reservas hoy"
          value={String(stats?.reservasHoy ?? 0)}
          sub="vs ayer"
          color="text-blue-600"
        />
        <KpiCard
          label="Reservas este mes"
          value={String(stats?.reservasMes ?? 0)}
          sub="Total activas"
          color="text-green-600"
        />
        <KpiCard
          label="Ingresos del mes"
          value={`${sym}${(stats?.ingresosMes ?? 0).toLocaleString('es-ES', { minimumFractionDigits: 2 })}`}
          sub="Confirmadas + completadas"
          color="text-purple-600"
        />
        <KpiCard
          label="Clientes activos"
          value={String(stats?.clientesActivos ?? 0)}
          sub={`Ocupación ${stats?.ocupacion ?? 0}%`}
          color="text-orange-500"
        />
      </div>

      {/* Gráfico reservas por día */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Reservas — últimos 30 días</h2>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={stats?.reservasPorDia || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradReservas" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="fecha" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} interval={4} />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 13 }}
              formatter={(v: any) => [v, "Reservas"]}
            />
            <Area type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2.5} fill="url(#gradReservas)" dot={false} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Gráfico ingresos + servicios top */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Ingresos por semana */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Ingresos — últimas 8 semanas</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats?.ingresosPorSemana || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 13 }}
                formatter={(v: any) => [`${sym}${Number(v).toFixed(2)}`, "Ingresos"]}
              />
              <Bar dataKey="ingresos" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Servicios top */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Servicios más reservados</h2>
          {stats?.serviciosTop && stats.serviciosTop.length > 0 ? (
            <div className="space-y-3">
              {stats.serviciosTop.map((s, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700 truncate">{s.nombre}</span>
                      <span className="text-sm font-bold text-gray-900 ml-2">{s.reservas}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                      <div
                        className="bg-blue-500 h-1.5 rounded-full"
                        style={{ width: `${Math.round((s.reservas / (stats.serviciosTop[0]?.reservas || 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0">{sym}{s.precio}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-400 text-sm">
              Sin datos este mes
            </div>
          )}
        </div>
      </div>

      {/* Estado de reservas + reservas recientes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Pie chart estados */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Estado de reservas</h2>
          {stats?.reservasPorEstado && stats.reservasPorEstado.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={stats.reservasPorEstado} dataKey="total" nameKey="estado" cx="50%" cy="50%" outerRadius={65} innerRadius={35}>
                    {stats.reservasPorEstado.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 12 }}
                    formatter={(v: any, name: any) => [v, STATUS_LABELS[name] || name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {stats.reservasPorEstado.map((e, i) => (
                  <div key={i} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: e.color }} />
                      <span className="text-gray-600">{STATUS_LABELS[e.estado] || e.estado}</span>
                    </div>
                    <span className="font-semibold text-gray-800">{e.total}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">Sin reservas este mes</div>
          )}
        </div>

        {/* Reservas recientes */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 lg:col-span-2 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Reservas recientes</h2>
          </div>
          {recentBookings.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Servicio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Negocio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Importe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentBookings.map((b) => (
                    <tr key={b.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">{b.service?.name || '—'}</td>
                      <td className="px-6 py-3 text-sm text-gray-500">{b.business?.name || '—'}</td>
                      <td className="px-6 py-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          b.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' :
                          b.status === 'COMPLETED' ? 'bg-blue-100 text-blue-700' :
                          b.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                          b.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {STATUS_LABELS[b.status] || b.status}
                        </span>
                      </td>
                      <td className="px-6 py-3 text-sm font-semibold text-gray-900">
                        {b.service?.price ? `${sym}${Number(b.service.price).toFixed(2)}` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">
              Sin reservas recientes
            </div>
          )}
        </div>
      </div>

      {/* Acciones rápidas */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Acciones rápidas</h2>
        <div className="flex flex-wrap gap-3">
          <a href="/businesses" className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors">
            Mis negocios
          </a>
          <a href="/services" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors">
            Servicios
          </a>
          <a href="/bookings" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors">
            Todas las reservas
          </a>
          <a href="/staff" className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-200 transition-colors">
            Personal
          </a>
        </div>
      </div>

    </div>
  );
}
